import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, Card } from 'react-bootstrap';
import { Modal, Radio } from 'antd';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import AppCard from 'shared/design-system/components/app-card';
import NoData from 'shared/design-system/components/no-data-box';
import ServerSidePaginator from 'shared/design-system/components/app-server-side-paginator';
import LinksButton from 'shared/design-system/components/links-button';
import { AgendaCommercialeDto, RptAgendaCommercialeDto, AgendaCommercialeSearchDto, SearchAgendaCommercialeRequest } from '@api/client';
import { AppToastService } from 'shared/design-system/components/app-toast/app-toast.service';
import { searchAgendaActions, saveFiltersAgendaActionsUsed, deleteAgendaAction, saveOrUpdateAgendaAction, getAgendaFilterOptions } from './_redux/actions';
import { filtersAgendaActionsUsedSelector, agendaFilterOptionsSelector } from './_redux/selectors';
import useGetAndMapAgendaActions from './_hooks/useGetAndMapAgendaActions';
import AgendaActionsTable from './_components/agenda-actions-table';
import AgendaActionsCalendar from './_components/agenda-actions-calendar';
import AgendaActionsUpcoming from './_components/agenda-actions-upcoming';
import AgendaActionsFilters from './_components/agenda-actions-filters';
import AddAgendaActionModal from './_components/add-agenda-action-modal';
import AgendaActionDetailModal from './_components/agenda-action-detail-modal';
import { exportAgendaActionsToExcel } from './_utils/exportAgendaActions';

const DEFAULT_PAGE_SIZE = 10;
const CALENDAR_PAGE_SIZE = 500;
type ViewMode = 'list' | 'calendar';

const stripPagingDates = (dto?: AgendaCommercialeSearchDto): AgendaCommercialeSearchDto => {
  if (!dto) return {};
  const { paging, actionDateDa, actionDateA, ...rest } = dto;
  void paging; void actionDateDa; void actionDateA;
  return rest;
};

const buildCalendarDto = (date: Date, base?: AgendaCommercialeSearchDto): AgendaCommercialeSearchDto => ({
  ...stripPagingDates(base),
  actionDateDa: dayjs(date).startOf('month').subtract(7, 'day').toDate(),
  actionDateA: dayjs(date).endOf('month').add(7, 'day').toDate(),
  paging: { page: 0, size: CALENDAR_PAGE_SIZE },
});

const buildListDto = (base?: AgendaCommercialeSearchDto, page = 0): AgendaCommercialeSearchDto => ({
  ...stripPagingDates(base),
  paging: { page, size: DEFAULT_PAGE_SIZE },
});

const AgendaActions: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const filtersUsed = useAppSelector(filtersAgendaActionsUsedSelector);
  const agendaFilterOptions = useAppSelector(agendaFilterOptionsSelector);
  const { actions, rawActions, paging } = useGetAndMapAgendaActions();

  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [editData, setEditData] = useState<RptAgendaCommercialeDto | undefined>(undefined);
  const [detailData, setDetailData] = useState<RptAgendaCommercialeDto | undefined>(undefined);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());

  useEffect(() => {
    if (!agendaFilterOptions?.accounts?.length) dispatch(getAgendaFilterOptions());
  }, []);

  useEffect(() => {
    const dto = viewMode === 'calendar'
      ? buildCalendarDto(calendarMonth, filtersUsed)
      : buildListDto(filtersUsed);
    dispatch(saveFiltersAgendaActionsUsed(dto));
    dispatch(searchAgendaActions({ agendaCommercialeSearchDto: dto }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, calendarMonth]);

  const handleSearch = (filters: Partial<AgendaCommercialeSearchDto>) => {
    const dto: AgendaCommercialeSearchDto = viewMode === 'calendar'
      ? buildCalendarDto(calendarMonth, filters as AgendaCommercialeSearchDto)
      : buildListDto(filters as AgendaCommercialeSearchDto);
    dispatch(saveFiltersAgendaActionsUsed(dto));
    dispatch(searchAgendaActions({ agendaCommercialeSearchDto: dto }));
  };

  const handleExportExcel = async () => {
    // Recupera tutte le attività filtrate iterando su tutte le pagine restituite dal BE
    const base = filtersUsed ?? {};
    const { paging, ...rest } = base;
    void paging;
    const EXPORT_PAGE_SIZE = 500;
    const all: RptAgendaCommercialeDto[] = [];
    try {
      let page = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const dto: AgendaCommercialeSearchDto = { ...rest, paging: { page, size: EXPORT_PAGE_SIZE } };
        const res = await dispatch(searchAgendaActions({ agendaCommercialeSearchDto: dto })).unwrap();
        const content = res?.content ?? [];
        all.push(...content);
        const totalPages = res?.totalPages ?? 1;
        page += 1;
        if (page >= totalPages || content.length === 0) break;
      }
      exportAgendaActionsToExcel(all, `agenda-attivita-${dayjs().format('YYYY-MM-DD')}`);
    } catch {
      AppToastService.error(t('Errore durante l\'export'));
    } finally {
      // Ripristina la paginazione originale della vista corrente
      if (filtersUsed) dispatch(searchAgendaActions({ agendaCommercialeSearchDto: filtersUsed }));
    }
  };

  const handleReset = () => {
    const dto: AgendaCommercialeSearchDto = viewMode === 'calendar'
      ? buildCalendarDto(calendarMonth)
      : { paging: { page: 0, size: DEFAULT_PAGE_SIZE } };
    dispatch(saveFiltersAgendaActionsUsed(dto));
    dispatch(searchAgendaActions({ agendaCommercialeSearchDto: dto }));
  };

  const refresh = () => {
    const dto = viewMode === 'calendar'
      ? buildCalendarDto(calendarMonth, filtersUsed)
      : (filtersUsed ?? buildListDto());
    dispatch(searchAgendaActions({ agendaCommercialeSearchDto: dto }));
  };

  const openCreateModal = (date?: Date) => {
    setEditData(undefined);
    setSelectedDate(date);
    setShowModal(true);
  };

  const handleEdit = (id: number) => {
    const item = rawActions.find((a) => a.id === id);
    if (!item) return;
    setEditData(item);
    setSelectedDate(undefined);
    setShowModal(true);
  };

  const handleViewDetail = (id: number) => {
    const item = rawActions.find((a) => a.id === id);
    if (!item) return;
    setDetailData(item);
    setShowDetailModal(true);
  };

  const executeDelete = (id: number) => {
    dispatch(deleteAgendaAction({ id }))
      .unwrap()
      .then(() => {
        AppToastService.success(t('Attività eliminata con successo'));
        refresh();
      });
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: t('Conferma eliminazione'),
      content: t('Sei sicuro di voler eliminare questa attività?'),
      okText: t('Elimina'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: () => executeDelete(id),
    });
  };

  const handleDropEvent = (item: RptAgendaCommercialeDto, newDate: Date) => {
    // Forza mezzogiorno locale per evitare shift di fuso orario in toISOString()
    const safeDate = new Date(newDate);
    safeDate.setHours(12, 0, 0, 0);
    dispatch(saveOrUpdateAgendaAction({
      agendaCommercialeDto: {
        id: item.id,
        fkCliente: item.clienteId,
        nominativoCliente: item.nominativoCliente,
        actionDate: safeDate,
        modalitaContatto: item.modalitaContatto,
        esito: item.esito,
        amount: item.amount,
        margin: item.margin,
        nota: item.nota,
      },
    }))
      .unwrap()
      .then(() => {
        AppToastService.success(t('Data aggiornata con successo'));
        refresh();
      });
  };

  const changePage = (page: number) => {
    const filters = { ...filtersUsed, paging: { ...filtersUsed?.paging, page, size: DEFAULT_PAGE_SIZE } };
    const payload: SearchAgendaCommercialeRequest = { agendaCommercialeSearchDto: filters };
    dispatch(saveFiltersAgendaActionsUsed(filters));
    dispatch(searchAgendaActions(payload));
  };

  const viewOptions = [
    {
      label: (
        <span>
          <i className="fas fa-list me-1" />
          {t('Lista')}
        </span>
      ),
      value: 'list',
    },
    {
      label: (
        <span>
          <i className="fas fa-calendar-alt me-1" />
          {t('Calendario')}
        </span>
      ),
      value: 'calendar',
    },
  ];

  return (
    <>
      <div className="agenda-actions-header">
        <h4 className="text-gray-800">{t('Azioni Agenda Commerciale')}</h4>
        <div className="agenda-actions-header__controls">
          <Radio.Group
            options={viewOptions}
            value={viewMode}
            optionType="button"
            onChange={(e) => setViewMode(e.target.value as ViewMode)}
          />
          <LinksButton className="btn-primary btn-links" onClick={() => openCreateModal()}>
            <i className="fas fa-plus me-2" />
            <span className="d-none d-sm-inline">{t('Nuova attività')}</span>
            <span className="d-inline d-sm-none">{t('Nuova')}</span>
          </LinksButton>
        </div>
      </div>

      {viewMode === 'list' && (
        <>
          <Accordion className="acc-btn my-3">
            <Card className="border-left-primary acc-btn">
              <Card.Header
                className={`cursor-pointer d-flex align-items-center card-header-border ${filtersOpen ? 'card-header-border' : ''}`}
                onClick={() => setFiltersOpen(!filtersOpen)}
              >
                <b className="text-blue-links my-auto ms-2 me-3">{t('cdg.filters')}</b>
                <div className="ms-auto">
                  <i className={`fa-solid fa-chevron-${filtersOpen ? 'up' : 'down'} notification-header`} />
                </div>
              </Card.Header>
              {filtersOpen && (
                <Card.Body className="mx-2">
                  <AgendaActionsFilters onSearch={handleSearch} onReset={handleReset} />
                </Card.Body>
              )}
            </Card>
          </Accordion>
          <AppCard
            title={t('Elenco azioni')}
            className="mt-3"
            headerElement={
              <LinksButton className="btn-sm btn btn-links-outline px-3 ms-3" onClick={handleExportExcel}>
                <i className="fa-solid fa-file-excel me-2" />
                {t('Export Excel')}
              </LinksButton>
            }
          >
          <div className="agenda-calendar-legend mb-3">
            <span className="agenda-calendar-legend__item">
              <span className="agenda-calendar-legend__swatch" style={{ backgroundColor: '#4caf50' }} />
              {t('Positivo')}
            </span>
            <span className="agenda-calendar-legend__item">
              <span className="agenda-calendar-legend__swatch" style={{ backgroundColor: '#f59e0b' }} />
              {t('Neutro')}
            </span>
            <span className="agenda-calendar-legend__item">
              <span className="agenda-calendar-legend__swatch" style={{ backgroundColor: '#ef4444' }} />
              {t('Negativo')}
            </span>
            <span className="agenda-calendar-legend__item">
              <span className="agenda-calendar-legend__swatch" style={{ backgroundColor: '#6c757d' }} />
              {t('Annullata')}
            </span>
          </div>
          {actions.length === 0 ? (
            <NoData msg={t('Nessuna azione trovata')} />
          ) : (
            <AgendaActionsTable
              data={actions}
              onDelete={handleDelete}
              onEdit={handleEdit}
              onViewDetail={handleViewDetail}
            />
          )}
          {actions.length > 0 && (
            <ServerSidePaginator
              currentPage={paging?.page || 0}
              totalPages={paging?.totalPages || 0}
              elementsToShow={actions.length}
              totalElements={paging?.totalElements}
              onPageChange={(e) => changePage(e)}
            />
          )}
          </AppCard>
        </>
      )}

      {viewMode === 'calendar' && (
        <div className="agenda-calendar-layout">
          <div className={`agenda-calendar-sidebar-col${mobileFiltersOpen ? ' agenda-calendar-sidebar-col--visible' : ''}`}>
            <AgendaActionsFilters
              onSearch={(f) => { handleSearch(f); setMobileFiltersOpen(false); }}
              onReset={() => { handleReset(); setMobileFiltersOpen(false); }}
              hideDateFilters
              calendarSpacer
            />
          </div>
          <div className="agenda-calendar-main-col">
            <button
              type="button"
              className="agenda-calendar-filter-toggle"
              onClick={() => setMobileFiltersOpen((o) => !o)}
            >
              <i className={`fas fa-${mobileFiltersOpen ? 'times' : 'filter'} me-2`} />
              {mobileFiltersOpen ? t('Nascondi filtri') : t('Filtri')}
            </button>
            <AgendaActionsCalendar
              data={rawActions}
              date={calendarMonth}
              onNavigate={setCalendarMonth}
              onAddAction={openCreateModal}
              onDropEvent={handleDropEvent}
              onSelectEvent={(item) => {
                setDetailData(item);
                setShowDetailModal(true);
              }}
            />
          </div>
          <div className="agenda-calendar-upcoming-col">
            <AgendaActionsUpcoming
              data={rawActions}
              onSelectEvent={(item) => {
                setDetailData(item);
                setShowDetailModal(true);
              }}
            />
          </div>
        </div>
      )}

      <AddAgendaActionModal
        show={showModal}
        setShow={setShowModal}
        defaultDate={selectedDate}
        editData={editData}
        onSaved={refresh}
      />

      <AgendaActionDetailModal
        show={showDetailModal}
        setShow={setShowDetailModal}
        data={detailData}
        onEdit={detailData?.id ? () => handleEdit(detailData.id!) : undefined}
        onDelete={detailData?.id ? () => executeDelete(detailData.id!) : undefined}
      />
    </>
  );
};

export default AgendaActions;
